import { Search, UserPlus } from 'lucide-react'

import { cn } from '@/lib/utils'
import MaterialSymbolsCheckRounded from '~icons/material-symbols/check-rounded'

import { CheckinLayout } from './CheckinLayout'
import { StepHeader } from './StepHeader'
import type { GuestItem } from './types'

interface SearchAllStepProps {
  onBack: () => void
  searchAllQuery: string
  onSearchAllQueryChange: (val: string) => void
  isFetching: boolean
  filteredAllGuests: GuestItem[]
  onSelectGuest: (guest: GuestItem) => void
  onManualRegister: () => void
}

export function SearchAllStep({
  onBack,
  searchAllQuery,
  onSearchAllQueryChange,
  isFetching,
  filteredAllGuests,
  onSelectGuest,
  onManualRegister,
}: SearchAllStepProps) {
  return (
    <CheckinLayout>
      <StepHeader
        title="ค้นหาจากทั้งหมด"
        onBack={onBack}
      />

      <div className="relative mb-4">
        <Search className="text-foreground/40 absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <input
          autoFocus
          type="text"
          aria-label="ค้นหาชื่อหรือกลุ่ม"
          placeholder="ค้นหาชื่อ หรือกลุ่ม..."
          value={searchAllQuery}
          onChange={(e) => onSearchAllQueryChange(e.target.value)}
          className="border-border focus:ring-primary/20 w-full rounded-2xl border bg-white/50 py-2.5 pr-4 pl-9 text-sm outline-none focus:ring-2"
        />
      </div>

      {isFetching ? (
        <div className="py-12 text-center opacity-50">กำลังโหลด...</div>
      ) : !searchAllQuery ? (
        <div className="py-12 text-center text-sm opacity-40">
          พิมพ์เพื่อค้นหา
        </div>
      ) : filteredAllGuests.length === 0 ? (
        <div className="py-8 text-center opacity-50">ไม่พบชื่อ</div>
      ) : (
        <div className="space-y-3">
          {filteredAllGuests.map((guest) => {
            const count = guest.checkedInCount || 0
            const capacity = guest.expectedFollowers
            const isFullyCheckedIn = count >= capacity
            const isPartiallyCheckedIn = count > 0 && !isFullyCheckedIn
            return (
              <button
                key={guest.id}
                type="button"
                onClick={() => onSelectGuest(guest)}
                className={cn(
                  'flex w-full items-center justify-between rounded-2xl border-2 px-5 py-4 text-left transition-all active:scale-[0.98]',
                  isFullyCheckedIn
                    ? 'border-green-300/60 bg-green-50/60 opacity-70'
                    : isPartiallyCheckedIn
                      ? 'border-amber-300/60 bg-amber-50/60 shadow-sm hover:bg-amber-50/80'
                      : 'border-white/60 bg-white/80 shadow-xl backdrop-blur-md hover:bg-white/90',
                )}
              >
                <div>
                  <p className="text-base leading-tight font-semibold">
                    {guest.fullName}
                  </p>
                  <p className="text-foreground/50 mt-0.5 text-xs">
                    {[
                      guest.group,
                      guest.side === 'groom'
                        ? 'ฝ่ายเจ้าบ่าว'
                        : guest.side === 'bride'
                          ? 'ฝ่ายเจ้าสาว'
                          : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <div className="ml-3 shrink-0">
                  {isFullyCheckedIn ? (
                    <span className="flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
                      <MaterialSymbolsCheckRounded className="size-3.5" />
                      {count}/{capacity}
                    </span>
                  ) : isPartiallyCheckedIn ? (
                    <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                      {count}/{capacity}
                    </span>
                  ) : (
                    <span className="text-foreground/40 text-xs">
                      {capacity} ท่าน
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      )}

      <div className="mt-8 border-t border-white/40 pt-6">
        <p className="text-foreground/50 mb-3 text-center text-sm">
          ไม่พบชื่อของคุณ?
        </p>
        <button
          type="button"
          onClick={onManualRegister}
          className="flex w-full items-center gap-3 rounded-2xl border-2 border-dashed border-white/60 bg-white/60 px-5 py-4 text-left backdrop-blur-sm transition-all hover:bg-white/80 active:scale-[0.98]"
        >
          <div className="bg-foreground/10 flex size-10 shrink-0 items-center justify-center rounded-xl">
            <UserPlus className="text-foreground/60 size-5" />
          </div>
          <div>
            <p className="text-sm font-semibold">ลงทะเบียนด้วยตนเอง</p>
            <p className="text-foreground/50 text-xs">
              กรอกชื่อและข้อมูลของคุณเอง
            </p>
          </div>
        </button>
      </div>
    </CheckinLayout>
  )
}
